import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LazyImage from '../components/LazyImage';
import { useCart } from '../contexts/CartContext';
import { useFlyingCart } from '../contexts/FlyingCartContext';
import { useToast } from '../contexts/ToastContext';
import { companyService, productService, type CompanyInfo } from '../services';
import { gtagService } from '../services/gtagService';
import type { Product } from '../types/Product';
import { useQuickBuyWorker } from '../hooks/useQuickBuyWorker';

const QuickBuy: React.FC = () => {
  const navigate = useNavigate();
  const { items: cart, addToCart, removeFromCart } = useCart();
  const { showToast } = useToast();
  const { triggerFlyingAnimation } = useFlyingCart();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [previewImage, setPreviewImage] = useState<{ 
    url: string; 
    name: string; 
    allImages: Array<{ url: string; type: string }>;
    currentIndex: number;
  } | null>(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const couponCode = 'UC10';
  const discountPercentage = 10;
  const productsPerPage = 200; // Maximum allowed by backend
  const { isWorkerSupported, filterProducts: filterProductsWorker, calculateTotals: calculateTotalsWorker, extractFilters: extractFiltersWorker } = useQuickBuyWorker();

  // Initial load - products and company info
  useEffect(() => {
    // Track quick buy access
    gtagService.trackQuickBuyAccess();
    
    const loadAllProducts = async () => {
      setIsLoading(true);
      try {
        // First, get company info and initial products to determine total
        const [initialProductsResponse, companyResponse] = await Promise.all([
          productService.getProducts({ sortBy: 'sku' as any }, { 
            page: 1, 
            limit: productsPerPage 
          }),
          companyService.getCompanyInfo()
        ]);
        
        if (companyResponse.success) {
          setCompanyInfo(companyResponse.data);
        }
        
        if (initialProductsResponse.success && initialProductsResponse.data) {
          const allProducts = [...initialProductsResponse.data.data];
          const totalPages = initialProductsResponse.data.totalPages;
          
          console.log('Initial products loaded:', {
            count: allProducts.length,
            totalPages,
            firstProduct: allProducts[0]
          });
          
          // If there are more pages, load them all
          if (totalPages > 1) {
            const remainingPages = [];
            for (let page = 2; page <= totalPages; page++) {
              remainingPages.push(
                productService.getProducts({ sortBy: 'sku' as any }, { 
                  page, 
                  limit: productsPerPage 
                })
              );
            }
            
            // Load all remaining pages in parallel
            const remainingResponses = await Promise.all(remainingPages);
            
            // Add all products from remaining pages
            remainingResponses.forEach(response => {
              if (response.success && response.data) {
                allProducts.push(...response.data.data);
              }
            });
          }
          
          console.log('All products loaded:', allProducts.length);
          setProducts(allProducts);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        showToast('Failed to load data', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    loadAllProducts();
  }, [showToast]);


  // Get quantity of a product in cart
  const getCartQuantity = (productId: string) => {
    const item = cart.find(item => item.id === productId);
    return item ? item.quantity : 0;
  };

  // Handle image click for preview
  const handleImageClick = (product: Product) => {
    // Get all media (images and videos) from medias array or fallback to main image
    const allImages = product.medias && product.medias.length > 0 
      ? product.medias.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)).map(media => ({ 
          url: media.url, 
          type: media.type 
        }))
      : [{ url: product.image, type: 'image' }];
    
    setPreviewImage({ 
      url: allImages[0].url, 
      name: product.name,
      allImages,
      currentIndex: 0
    });
  };

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  // Handle navigation between images
  const handlePrevImage = () => {
    if (previewImage && previewImage.currentIndex > 0) {
      setPreviewImage({
        ...previewImage,
        currentIndex: previewImage.currentIndex - 1,
        url: previewImage.allImages[previewImage.currentIndex - 1].url
      });
    }
  };

  const handleNextImage = () => {
    if (previewImage && previewImage.currentIndex < previewImage.allImages.length - 1) {
      setPreviewImage({
        ...previewImage,
        currentIndex: previewImage.currentIndex + 1,
        url: previewImage.allImages[previewImage.currentIndex + 1].url
      });
    }
  };

  // Calculate price after discount
  const calculateDiscountedPrice = (price: number) => {
    return price - (price * discountPercentage / 100);
  };

  // Extract unique brands and categories
  const [brands, setBrands] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  
  // Extract filters when products change
  useEffect(() => {
    if (products.length > 0) {
      if (false && isWorkerSupported) { // Temporarily disabled
        extractFiltersWorker(products).then(({ brands, categories }) => {
          setBrands(brands);
          setCategories(categories);
        });
      } else {
        // Fallback for immediate calculation
        const uniqueBrands = [...new Set(products.map(p => p.brand || 'Demo Crackers'))].sort();
        const uniqueCategories = [...new Set(products.map(p => p.category))].filter(Boolean).sort();
        setBrands(uniqueBrands);
        setCategories(uniqueCategories);
      }
    }
  }, [products, isWorkerSupported, extractFiltersWorker]);

  // Filter products state
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [inStockProducts, setInStockProducts] = useState<Product[]>([]);
  const [outOfStockProducts, setOutOfStockProducts] = useState<Product[]>([]);
  
  // Filter products using worker or fallback
  useEffect(() => {
    const filterProductsAsync = async () => {
      if (products.length === 0) {
        setFilteredProducts([]);
        setInStockProducts([]);
        setOutOfStockProducts([]);
        return;
      }
      
      try {
        if (false && isWorkerSupported) { // Temporarily disabled
          console.log('Using web worker for filtering', { productsCount: products.length });
          const result = await filterProductsWorker(products, searchTerm, selectedBrand, selectedCategory);
          console.log('Worker result:', result);
          setFilteredProducts(result.filteredProducts);
          setInStockProducts(result.inStockProducts);
          setOutOfStockProducts(result.outOfStockProducts);
        } else {
          console.log('Using fallback filtering');
        // Immediate fallback calculation
        const filtered = products.filter(product => {
          const matchesSearch = !searchTerm || 
            product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase()));
          
          const matchesBrand = !selectedBrand || 
            (product.brand || 'Demo Crackers') === selectedBrand;
          
          const matchesCategory = !selectedCategory || 
            product.category === selectedCategory;
          
          return matchesSearch && matchesBrand && matchesCategory;
        });
        
          setFilteredProducts(filtered);
          setInStockProducts(filtered.filter(p => p.inStock));
          setOutOfStockProducts(filtered.filter(p => !p.inStock));
        }
      } catch (error) {
        console.error('Error filtering products:', error);
        // Fallback to synchronous filtering on error
        const filtered = products.filter(product => {
          const matchesSearch = !searchTerm || 
            product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase()));
          
          const matchesBrand = !selectedBrand || 
            (product.brand || 'Demo Crackers') === selectedBrand;
          
          const matchesCategory = !selectedCategory || 
            product.category === selectedCategory;
          
          return matchesSearch && matchesBrand && matchesCategory;
        });
        
        setFilteredProducts(filtered);
        setInStockProducts(filtered.filter(p => p.inStock));
        setOutOfStockProducts(filtered.filter(p => !p.inStock));
      }
    };
    
    filterProductsAsync();
  }, [products, searchTerm, selectedBrand, selectedCategory, isWorkerSupported, filterProductsWorker]);

  // Calculate totals state
  const [cartSummary, setCartSummary] = useState({
    items: [] as typeof cart,
    subtotal: 0,
    originalSubtotal: 0,
    totalItems: 0,
    totalSavings: 0
  });
  
  // Calculate totals using worker or fallback
  useEffect(() => {
    const calculateTotalsAsync = async () => {
      if (cart.length === 0) {
        setCartSummary({
          items: [],
          subtotal: 0,
          originalSubtotal: 0,
          totalItems: 0,
          totalSavings: 0
        });
        return;
      }
      
      if (false && isWorkerSupported) { // Temporarily disabled
        const totals = await calculateTotalsWorker(cart, discountPercentage);
        setCartSummary({
          items: cart,
          originalSubtotal: 0, // Add missing property
          ...totals
        });
      } else {
        // Immediate fallback calculation
        const subtotal = cart.reduce((sum, item) => {
          return sum + (calculateDiscountedPrice(item.price) * item.quantity);
        }, 0);

        const originalSubtotal = cart.reduce((sum, item) => {
          return sum + (item.price * item.quantity);
        }, 0);

        const totalSavings = cart.reduce((sum, item) => {
          return sum + ((item.price * discountPercentage / 100) * item.quantity);
        }, 0);

        const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

        setCartSummary({
          items: cart,
          subtotal,
          originalSubtotal,
          totalItems,
          totalSavings
        });
      }
    };
    
    calculateTotalsAsync();
  }, [cart, discountPercentage, isWorkerSupported, calculateTotalsWorker]);

  // Handle quantity change - memoized to prevent recreations
  const handleQuantityChange = useCallback((product: Product, newQuantity: number, element?: HTMLElement) => {
    const currentQuantity = getCartQuantity(product.id);
    
    if (newQuantity < 0) return;
    
    // Enforce max limit of 9999
    if (newQuantity > 9999) {
      showToast('Maximum quantity is 9,999', 'error');
      return;
    }
    
    // Calculate total cart items after this change
    const totalItems = cartSummary.totalItems - currentQuantity + newQuantity;
    if (totalItems > 10000) {
      showToast('Total cart items cannot exceed 10,000', 'error');
      return;
    }
    
    if (newQuantity === 0) {
      removeFromCart(product.id);
    } else if (newQuantity !== currentQuantity) {
      // Since CartContext manages quantity internally, we need to add/remove multiple times
      const diff = newQuantity - currentQuantity;
      if (diff > 0) {
        // Trigger flying animation for increases
        if (element) {
          const rect = element.getBoundingClientRect();
          const startPosition = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
          };
          triggerFlyingAnimation(startPosition, product.image);
        }
        
        for (let i = 0; i < diff; i++) {
          addToCart(product);
        }
      } else {
        // For now, remove and re-add to set exact quantity
        removeFromCart(product.id);
        for (let i = 0; i < newQuantity; i++) {
          addToCart(product);
        }
      }
    }
  }, [cartSummary.totalItems, getCartQuantity, removeFromCart, addToCart, triggerFlyingAnimation, showToast, cart]);

  // Handle direct quantity input change - memoized
  const handleQuantityInputChange = useCallback((product: Product, inputValue: string) => {
    // Allow empty string for editing
    if (inputValue === '') {
      return;
    }
    
    // Parse the input value
    const newQuantity = parseInt(inputValue, 10);
    
    // Validate input
    if (isNaN(newQuantity) || newQuantity < 0) {
      return;
    }
    
    // Enforce max limit of 9999 (4 digits)
    if (newQuantity > 9999) {
      showToast('Maximum quantity is 9,999', 'error');
      return;
    }
    
    handleQuantityChange(product, newQuantity, undefined);
  }, [showToast, handleQuantityChange]);

  // Handle increment/decrement - memoized to prevent recreations
  const handleIncrement = useCallback((product: Product, event?: React.MouseEvent<HTMLButtonElement>) => {
    // Check total cart limit
    if (cartSummary.totalItems >= 10000) {
      showToast('Cart limit reached (10,000 items max)', 'error');
      return;
    }
    
    // Get button position for flying animation
    if (event && event.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      const startPosition = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };
      triggerFlyingAnimation(startPosition, product.image);
    }
    
    addToCart(product);
  }, [cartSummary.totalItems, showToast, triggerFlyingAnimation, addToCart]);

  const handleDecrement = useCallback((product: Product) => {
    const currentQty = getCartQuantity(product.id);
    if (currentQty > 1) {
      // Decrease quantity by 1
      const item = cart.find(item => item.id === product.id);
      if (item) {
        // Remove and re-add with new quantity
        removeFromCart(product.id);
        for (let i = 0; i < currentQty - 1; i++) {
          addToCart(product);
        }
      }
    } else if (currentQty === 1) {
      removeFromCart(product.id);
    }
  }, [getCartQuantity, cart, removeFromCart, addToCart]);

  const handleProceedToCart = useCallback(() => {
    if (cart.length === 0) {
      showToast('Please add at least one product', 'error');
      return;
    }
    navigate('/checkout');
  }, [cart, showToast, navigate]);

  const handleWhatsAppClick = useCallback(() => {
    // Don't proceed if company info not loaded
    if (!companyInfo) {
      showToast('Please wait, loading contact information...', 'info');
      return;
    }

    // Detect if mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    // Create cart summary for WhatsApp message
    const cartItems = cart.map(item => `${item.name} x ${item.quantity}`).join(', ');
    const totalAmount = cartSummary.subtotal;
    
    const whatsappMessage = 'Hi, planning to celebrate this Diwali with Demo Crackers. Need some guidance to buy the crackers.\n\n';
    const fullMessage = cartItems.length > 0 
      ? `${whatsappMessage}\n\nMy cart:\n${cartItems}\n\nTotal: ₹${Math.round(totalAmount).toLocaleString()}`
      : whatsappMessage;
    
    const encodedMessage = encodeURIComponent(fullMessage);
    const whatsappNumber = companyInfo.whatsappNumber;
    const whatsappUrl = isMobile 
      ? `https://wa.me/${whatsappNumber}?text=${encodedMessage}`
      : `https://web.whatsapp.com/send?phone=${whatsappNumber}&text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  }, [companyInfo, cart, cartSummary.subtotal]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="px-5 py-6 sm:py-8 lg:py-10">

        {/* Products Table */}
        <div className="pr-0 xl:pr-72 pb-20 xl:pb-0">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b border-neutral-200">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-neutral-900">
                      All Products
                      {filteredProducts.length < products.length && (
                        <span className="text-sm font-normal text-neutral-600 ml-2">
                          ({filteredProducts.length} of {products.length})
                        </span>
                      )}
                    </h2>
                  </div>
                  
                  {/* Search and Filters */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    {/* Search */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search by name or SKU..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 pl-9 border border-neutral-200 rounded-lg text-sm focus:border-accent-500 focus:ring-1 focus:ring-accent-200 outline-none transition-all duration-200"
                      />
                      <svg className="absolute left-3 top-2.5 w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    
                    {/* Brand Filter */}
                    <select
                      value={selectedBrand}
                      onChange={(e) => setSelectedBrand(e.target.value)}
                      className="px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:border-accent-500 focus:ring-1 focus:ring-accent-200 outline-none transition-all duration-200"
                    >
                      <option value="">All Brands</option>
                      {brands.map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>
                    
                    {/* Category Filter */}
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:border-accent-500 focus:ring-1 focus:ring-accent-200 outline-none transition-all duration-200"
                    >
                      <option value="">All Categories</option>
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                    
                    {/* Clear Filters */}
                    {(searchTerm || selectedBrand || selectedCategory) && (
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setSelectedBrand('');
                          setSelectedCategory('');
                        }}
                        className="px-3 py-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-lg text-sm font-medium transition-colors duration-200"
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto xl:overflow-x-visible">
                <table className="w-full table-fixed xl:table-auto">
                  <thead className="bg-neutral-50 border-b border-neutral-200 sticky top-0 z-10">
                    <tr>
                      {/* Mobile headers */}
                      <th className="px-3 py-2 text-left text-xs font-semibold text-neutral-600 uppercase xl:hidden w-1/2">Product Details</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-neutral-600 uppercase xl:hidden w-1/4">Qty</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-neutral-600 uppercase xl:hidden w-1/4">Amount</th>
                      
                      {/* Desktop headers */}
                      <th className="hidden xl:table-cell px-3 py-2 text-left text-xs font-semibold text-neutral-600 uppercase">Sno</th>
                      <th className="hidden xl:table-cell px-3 py-2 text-left text-xs font-semibold text-neutral-600 uppercase">Code</th>
                      <th className="hidden xl:table-cell px-3 py-2 text-left text-xs font-semibold text-neutral-600 uppercase">Name</th>
                      <th className="hidden xl:table-cell px-3 py-2 text-left text-xs font-semibold text-neutral-600 uppercase">Brand</th>
                      <th className="hidden xl:table-cell px-3 py-2 text-left text-xs font-semibold text-neutral-600 uppercase">Category</th>
                      <th className="hidden xl:table-cell px-3 py-2 text-left text-xs font-semibold text-neutral-600 uppercase">Pack</th>
                      <th className="hidden xl:table-cell px-3 py-2 text-left text-xs font-semibold text-neutral-600 uppercase">Pieces</th>
                      <th className="hidden xl:table-cell px-3 py-2 text-left text-xs font-semibold text-neutral-600 uppercase">Selling Price</th>
                      <th className="hidden xl:table-cell px-3 py-2 text-left text-xs font-semibold text-neutral-600 uppercase">After Coupon</th>
                      <th className="hidden xl:table-cell px-3 py-2 text-center text-xs font-semibold text-neutral-600 uppercase">Qty</th>
                      <th className="hidden xl:table-cell px-3 py-2 text-right text-xs font-semibold text-neutral-600 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {/* In Stock Products */}
                    {(() => {
                      let currentCategory = '';
                      let serialNumber = 0;
                      
                      return inStockProducts.map((product) => {
                        serialNumber++;
                        const quantity = getCartQuantity(product.id);
                        const discountedPrice = calculateDiscountedPrice(product.price);
                        const savings = product.price - discountedPrice;
                        const totalAmount = discountedPrice * quantity;
                        
                        const rows = [];
                        
                        // Add category row if category changed
                        if (product.category !== currentCategory) {
                          currentCategory = product.category;
                          rows.push(
                            <tr key={`category-${product.category}`} className="bg-accent-50">
                              <td colSpan={3} className="px-3 py-2 xl:hidden">
                                <span className="text-sm font-bold text-accent-800 uppercase">
                                  {product.category}
                                </span>
                              </td>
                              <td colSpan={11} className="hidden xl:table-cell px-3 py-2">
                                <span className="text-sm font-bold text-accent-800 uppercase">
                                  {product.category}
                                </span>
                              </td>
                            </tr>
                          );
                        }

                        rows.push(
                        <tr key={product.id} className="hover:bg-neutral-50 transition-colors duration-200">
                          {/* Mobile View - Combined Product Info */}
                          <td className="px-3 py-2 xl:hidden">
                            <div className="flex items-start gap-2">
                              <LazyImage
                                src={product.image}
                                alt={product.name}
                                className="w-10 h-10 object-cover rounded flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => handleImageClick(product)}
                              />
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-semibold text-neutral-900 line-clamp-2">
                                  {product.name}
                                </p>
                                <p className="text-xs text-neutral-500">
                                  {product.sku || '-'}
                                  {product.piecesPerBox && product.piecesPerBox > 1 && (
                                    <>
                                      <br />
                                      <span className="text-accent-600 font-medium">
                                        {product.piecesPerBox} pcs/box
                                      </span>
                                    </>
                                  )}
                                </p>
                                <p className="text-xs font-bold text-green-600">
                                  ₹{Math.round(discountedPrice).toLocaleString()}
                                  <span className="text-xs text-neutral-400 line-through ml-1">
                                    ₹{product.price.toLocaleString()}
                                  </span>
                                </p>
                              </div>
                            </div>
                          </td>
                          
                          {/* Mobile - Quantity */}
                          <td className="px-1 py-2 xl:hidden">
                            <div className="flex items-center justify-center gap-0.5">
                              <button
                                onClick={() => handleDecrement(product)}
                                disabled={quantity === 0}
                                className="w-8 h-8 rounded-lg bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-300 flex items-center justify-center text-lg font-bold text-neutral-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                              >
                                −
                              </button>
                              <input
                                type="text"
                                value={quantity || ''}
                                onChange={(e) => handleQuantityInputChange(product, e.target.value)}
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    handleQuantityChange(product, 0, e.currentTarget);
                                  }
                                }}
                                className="w-10 h-8 text-center border border-neutral-200 rounded-lg font-semibold text-sm focus:border-accent-500 focus:ring-1 focus:ring-accent-200 outline-none transition-all duration-200"
                                placeholder="0"
                                maxLength={4}
                              />
                              <button
                                onClick={(e) => handleIncrement(product, e)}
                                disabled={quantity >= 9999}
                                className="w-8 h-8 rounded-lg bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-300 flex items-center justify-center text-lg font-bold text-neutral-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                              >
                                +
                              </button>
                            </div>
                          </td>
                          
                          {/* Mobile - Amount */}
                          <td className="px-3 py-2 text-right xl:hidden">
                            <p className="font-bold text-sm text-neutral-900">
                              {quantity > 0 ? `₹${Math.round(totalAmount).toLocaleString()}` : '₹0'}
                            </p>
                          </td>
                          
                          {/* Desktop columns - all hidden on mobile */}
                          <td className="hidden xl:table-cell px-3 py-2 text-sm text-neutral-900">
                            {serialNumber}
                          </td>
                          
                          <td className="hidden xl:table-cell px-3 py-2 text-xs font-mono text-neutral-600">
                            {product.sku || '-'}
                          </td>
                          
                          <td className="hidden xl:table-cell px-3 py-2">
                            <div className="flex items-center gap-2">
                              <LazyImage
                                src={product.image}
                                alt={product.name}
                                className="w-8 h-8 object-cover rounded flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => handleImageClick(product)}
                              />
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-neutral-900 truncate">
                                  {product.name}
                                </p>
                              </div>
                            </div>
                          </td>
                          
                          <td className="hidden xl:table-cell px-3 py-2 text-xs text-neutral-600">
                            {product.brand || 'Demo Crackers'}
                          </td>
                          
                          <td className="hidden xl:table-cell px-3 py-2 text-xs text-neutral-600 capitalize">
                            {product.category}
                          </td>
                          
                          <td className="hidden xl:table-cell px-3 py-2 text-xs text-neutral-600">
                            {product.packSize || 'Box'}
                          </td>
                          
                          <td className="hidden xl:table-cell px-3 py-2 text-xs text-neutral-600">
                            {product.piecesPerBox || '-'}
                          </td>
                          
                          <td className="hidden xl:table-cell px-3 py-2 text-xs text-neutral-900">
                            <span className="font-semibold">₹{product.price.toLocaleString()}</span>
                          </td>
                          
                          <td className="hidden xl:table-cell px-3 py-2 text-xs">
                            <div>
                              <span className="font-semibold text-green-600">₹{Math.round(discountedPrice).toLocaleString()}</span>
                              {savings > 0 && (
                                <div className="text-xs text-green-600">
                                  {Math.round((savings / product.price) * 100)}% OFF
                                </div>
                              )}
                            </div>
                          </td>
                          
                          <td className="hidden xl:table-cell px-3 py-2">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleDecrement(product)}
                                disabled={quantity === 0}
                                className="w-6 h-6 rounded bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center text-sm font-bold text-neutral-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                −
                              </button>
                              <input
                                type="text"
                                value={quantity || ''}
                                onChange={(e) => handleQuantityInputChange(product, e.target.value)}
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    handleQuantityChange(product, 0, e.currentTarget);
                                  }
                                }}
                                className="w-12 h-6 text-center border border-neutral-200 rounded font-semibold text-xs focus:border-accent-500 focus:ring-1 focus:ring-accent-200 outline-none transition-all duration-200"
                                placeholder="0"
                                maxLength={4}
                              />
                              <button
                                onClick={(e) => handleIncrement(product, e)}
                                disabled={quantity >= 9999}
                                className="w-6 h-6 rounded bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center text-sm font-bold text-neutral-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                +
                              </button>
                            </div>
                          </td>
                          
                          <td className="hidden xl:table-cell px-3 py-2 text-right">
                            <div className="font-bold text-sm text-neutral-900">
                              {quantity > 0 ? `₹${Math.round(totalAmount).toLocaleString()}` : '₹0'}
                            </div>
                          </td>
                        </tr>
                        );
                        
                        return rows;
                      }).flat();
                    })()}
                    
                    {/* Out of Stock Section */}
                    {outOfStockProducts.length > 0 && (
                      <>
                        <tr className="bg-red-50">
                          <td colSpan={3} className="px-3 py-3 text-center xl:hidden">
                            <span className="text-sm font-semibold text-red-600">
                              😔 You have missed these items
                            </span>
                          </td>
                          <td colSpan={11} className="hidden xl:table-cell px-3 py-3 text-center">
                            <span className="text-sm font-semibold text-red-600">
                              😔 You have missed these items
                            </span>
                          </td>
                        </tr>
                        {outOfStockProducts.map((product, index) => {
                          const discountedPrice = calculateDiscountedPrice(product.price);
                          const serialNumber = inStockProducts.length + index + 1;

                          return (
                            <tr key={product.id} className="opacity-60 bg-neutral-50">
                              {/* Mobile View - Out of Stock Product */}
                              <td className="px-3 py-2 xl:hidden">
                                <div className="flex items-start gap-2">
                                  <LazyImage
                                    src={product.image}
                                    alt={product.name}
                                    className="w-10 h-10 object-cover rounded flex-shrink-0 opacity-50 cursor-pointer hover:opacity-30 transition-opacity"
                                    onClick={() => handleImageClick(product)}
                                  />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs font-medium text-neutral-600 line-clamp-2">
                                      {product.name}
                                    </p>
                                    <p className="text-xs text-neutral-500">
                                      {product.sku || '-'}
                                      {product.piecesPerBox && product.piecesPerBox > 1 && (
                                        <>
                                          <br />
                                          <span className="text-accent-600 font-medium">
                                            {product.piecesPerBox} pcs/box
                                          </span>
                                        </>
                                      )}
                                    </p>
                                    <span className="inline-block bg-red-100 text-red-800 text-xs px-1 py-0.5 rounded mt-1">
                                      Out of Stock
                                    </span>
                                  </div>
                                </div>
                              </td>
                              
                              {/* Mobile - Quantity */}
                              <td className="px-2 py-2 xl:hidden">
                                <div className="flex items-center justify-center">
                                  <span className="text-xs text-red-600 font-medium">Unavailable</span>
                                </div>
                              </td>
                              
                              {/* Mobile - Amount */}
                              <td className="px-3 py-2 text-right xl:hidden">
                                <p className="text-sm text-neutral-500">-</p>
                              </td>
                              
                              {/* Desktop columns */}
                              <td className="hidden xl:table-cell px-3 py-2 text-sm text-neutral-500">
                                {serialNumber}
                              </td>
                              
                              <td className="hidden xl:table-cell px-3 py-2 text-xs font-mono text-neutral-500">
                                {product.sku || '-'}
                              </td>
                              
                              <td className="hidden xl:table-cell px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <LazyImage
                                    src={product.image}
                                    alt={product.name}
                                    className="w-8 h-8 object-cover rounded flex-shrink-0 opacity-50"
                                  />
                                  <div className="min-w-0">
                                    <p className="text-xs font-medium text-neutral-600 truncate">
                                      {product.name}
                                    </p>
                                    <span className="inline-block bg-red-100 text-red-800 text-xs px-1 py-0.5 rounded">
                                      Out of Stock
                                    </span>
                                  </div>
                                </div>
                              </td>
                              
                              <td className="hidden xl:table-cell px-3 py-2 text-xs text-neutral-500">
                                {product.brand || 'Demo Crackers'}
                              </td>
                              
                              <td className="hidden xl:table-cell px-3 py-2 text-xs text-neutral-500 capitalize">
                                {product.category}
                              </td>
                              
                              <td className="hidden xl:table-cell px-3 py-2 text-xs text-neutral-500">
                                {product.packSize || 'Box'}
                              </td>
                              
                              <td className="hidden xl:table-cell px-3 py-2 text-xs text-neutral-500">
                                {product.piecesPerBox || '-'}
                              </td>
                              
                              <td className="hidden xl:table-cell px-3 py-2 text-xs text-neutral-500 line-through">
                                ₹{product.price.toLocaleString()}
                              </td>
                              
                              <td className="hidden xl:table-cell px-3 py-2 text-xs text-neutral-500 line-through">
                                ₹{Math.round(discountedPrice).toLocaleString()}
                              </td>
                              
                              <td className="hidden xl:table-cell px-3 py-2 text-center">
                                <span className="text-xs text-red-600">Unavailable</span>
                              </td>
                              
                              <td className="hidden xl:table-cell px-3 py-2 text-right">
                                <span className="text-xs text-neutral-500">-</span>
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </tbody>
                </table>
                
                {/* No results message */}
                {!isLoading && filteredProducts.length === 0 && (
                  <div className="p-8 text-center">
                    <div className="text-4xl mb-3">🔍</div>
                    <p className="text-lg font-semibold text-neutral-700 mb-2">
                      No products found
                    </p>
                    <p className="text-sm text-neutral-500 mb-4">
                      Try adjusting your search or filters
                    </p>
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedBrand('');
                        setSelectedCategory('');
                      }}
                      className="px-4 py-2 bg-accent-600 hover:bg-accent-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                    >
                      Clear All Filters
                    </button>
                  </div>
                )}
              </div>
              
              {/* All products loaded message */}
              {/* {!isLoading && products.length > 0 && (
                <div className="p-4 text-center border-t border-neutral-200">
                  <p className="text-sm text-neutral-500">
                    All {totalProducts} products loaded
                  </p>
                </div>
              )} */}
            </div>
        </div>

        {/* Floating Cart Summary - Bottom on mobile, side on desktop */}
        <div className="fixed bottom-0 left-0 right-0 xl:bottom-auto xl:top-20 xl:left-auto xl:right-5 w-full xl:w-64 z-10">
          <div className="bg-white rounded-t-2xl xl:rounded-xl shadow-lg border border-neutral-200 p-4">
            <h3 className="hidden xl:flex text-sm font-bold text-neutral-900 mb-3 items-center justify-between">
                Cart Summary
                {cartSummary.totalItems > 0 && (
                  <span className="bg-accent-100 text-accent-800 text-xs font-medium px-1.5 py-0.5 rounded-full">
                    {cartSummary.totalItems}
                  </span>
                )}
              </h3>

              {cartSummary.totalItems === 0 ? (
                <div className="text-center py-2 xl:py-4">
                  <div className="text-2xl mb-1 xl:mb-2">🛒</div>
                  <p className="text-xs text-neutral-500 mb-1">Your cart is empty</p>
                  <p className="text-xs text-neutral-400 hidden xl:block">Add products</p>
                </div>
              ) : (
                <>
                  {/* Mobile View - Compact */}
                  <div className="xl:hidden">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <span className="text-lg font-bold text-neutral-900">{cartSummary.totalItems}</span>
                            <span className="text-sm text-neutral-600">items</span>
                          </div>
                          <div className="h-5 w-px bg-neutral-300"></div>
                          <div>
                            <div className="text-lg font-bold text-neutral-900">
                              ₹{Math.round(cartSummary.originalSubtotal).toLocaleString()}
                            </div>
                            {cartSummary.totalSavings > 0 && (
                              <div className="text-xs text-green-600">
                                Save ₹{Math.round(cartSummary.totalSavings).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={handleProceedToCart}
                        className="bg-accent-600 text-white py-2.5 px-6 rounded-xl font-bold text-sm hover:bg-accent-700 transition-all duration-300 hover:shadow-md flex items-center gap-2"
                      >
                        View Enquiry
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Desktop View - Detailed */}
                  <div className="hidden xl:block">
                    {/* Cart Items */}
                    <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                      {cartSummary.items.map((item) => (
                        <div key={item.id} className="flex items-center gap-2 p-2 bg-neutral-50 rounded">
                          <LazyImage
                            src={item.image}
                            alt={item.name}
                            className="w-8 h-8 object-cover rounded flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-xs text-neutral-900 truncate">{item.name}</p>
                            <p className="text-xs text-neutral-600">
                              {item.quantity} × ₹{Math.round(item.price).toLocaleString()}
                            </p>
                          </div>
                          <p className="font-bold text-xs">
                            ₹{Math.round(item.price * item.quantity).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Summary */}
                    <div className="space-y-2 border-t pt-3 text-xs">
                      <div className="flex justify-between">
                        <span>Original Price ({cartSummary.totalItems})</span>
                        <span>₹{Math.round(cartSummary.originalSubtotal).toLocaleString()}</span>
                      </div>
                      {cartSummary.totalSavings > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>After coupon applied you will save</span>
                          <span>-₹{Math.round(cartSummary.totalSavings).toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold border-t pt-2 text-sm">
                        <span>Total</span>
                        <span>₹{Math.round(cartSummary.subtotal).toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-4">
                      <button
                        onClick={handleProceedToCart}
                        className="w-full bg-accent-600 text-white py-2 px-3 rounded-lg font-bold text-xs hover:bg-accent-700 transition-all duration-300 hover:shadow-md"
                      >
                        Proceed to Enquiry
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-12 mb-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl p-6 text-center shadow-sm">
            <div className="text-3xl mb-3">⚡</div>
            <h3 className="font-bold text-lg text-neutral-900 mb-2">Lightning Fast</h3>
            <p className="text-neutral-600">Add products instantly to your cart</p>
          </div>
          <div className="bg-white rounded-xl p-6 text-center shadow-sm">
            <div className="text-3xl mb-3">💰</div>
            <h3 className="font-bold text-lg text-neutral-900 mb-2">Special Discount</h3>
            <p className="text-neutral-600">{discountPercentage}% OFF with code {couponCode}</p>
          </div>
          <div className="bg-white rounded-xl p-6 text-center shadow-sm">
            <div className="text-3xl mb-3">🚚</div>
            <h3 className="font-bold text-lg text-neutral-900 mb-2">Lorry Service Delivery</h3>
            <p className="text-neutral-600">Shipping charges decided by lorry service</p>
          </div>
        </div>
        
        {/* Floating Search Button */}
        <button
          onClick={() => setShowSearchModal(true)}
          className="fixed bottom-40 right-5 z-50 bg-accent-600 hover:bg-accent-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 w-14 h-14 flex items-center justify-center"
          title="Quick Search"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>

        {/* WhatsApp Chat Button */}
        <button
          onClick={handleWhatsAppClick}
          className="fixed bottom-24 right-5 xl:bottom-8 xl:right-8 z-40 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          title="Chat with us on WhatsApp"
        >
          <div className="flex items-center gap-2 px-5 py-3">
            {/* WhatsApp Icon */}
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            <span className="font-semibold text-sm whitespace-nowrap">Chat with us</span>
          </div>
        </button>

        {/* Quick Search Modal */}
        {showSearchModal && (
          <div 
            className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
            onClick={() => setShowSearchModal(false)}
          >
            <div 
              className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-neutral-900">Search & Filter</h3>
                <button
                  onClick={() => setShowSearchModal(false)}
                  className="p-2 hover:bg-neutral-100 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Search Input */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Search</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Type product name or SKU..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-3 pl-10 border border-neutral-200 rounded-lg text-sm focus:border-accent-500 focus:ring-1 focus:ring-accent-200 outline-none transition-all duration-200"
                      autoFocus
                    />
                    <svg className="absolute left-3 top-3.5 w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                {/* Brand Filter */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Brand</label>
                  <select
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    className="w-full px-3 py-3 border border-neutral-200 rounded-lg text-sm focus:border-accent-500 focus:ring-1 focus:ring-accent-200 outline-none transition-all duration-200"
                  >
                    <option value="">All Brands</option>
                    {brands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>

                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 py-3 border border-neutral-200 rounded-lg text-sm focus:border-accent-500 focus:ring-1 focus:ring-accent-200 outline-none transition-all duration-200"
                  >
                    <option value="">All Categories</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                {/* Results Count */}
                <div className="text-sm text-neutral-600 text-center py-2 bg-neutral-50 rounded-lg">
                  <span className="font-semibold">{filteredProducts.length}</span> products found
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2">
                  {(searchTerm || selectedBrand || selectedCategory) && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedBrand('');
                        setSelectedCategory('');
                      }}
                      className="flex-1 px-4 py-2.5 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-lg text-sm font-medium transition-colors duration-200"
                    >
                      Clear All
                    </button>
                  )}
                  <button
                    onClick={() => setShowSearchModal(false)}
                    className="flex-1 px-4 py-2.5 bg-accent-600 hover:bg-accent-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                  >
                    Apply & Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Preview Modal */}
        {previewImage && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
            onClick={() => setPreviewImage(null)}
          >
            <div className="relative max-w-5xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
              {/* Close button */}
              <button
                onClick={() => setPreviewImage(null)}
                className="absolute -top-12 right-0 text-white hover:text-accent-400 transition-colors z-10"
              >
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Main content area */}
              <div className="relative flex items-center justify-center">
                {/* Previous button */}
                {previewImage.allImages.length > 1 && previewImage.currentIndex > 0 && (
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-0 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-3 rounded-r-lg transition-all duration-200 z-10"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}

                {/* Image or Video */}
                {previewImage.allImages[previewImage.currentIndex].type === 'video' ? (
                  <div className="w-full max-w-4xl aspect-video">
                    <iframe
                      src={`https://www.youtube.com/embed/${getYouTubeVideoId(previewImage.url)}`}
                      title={previewImage.name}
                      className="w-full h-full rounded-lg"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <img
                    src={previewImage.url}
                    alt={previewImage.name}
                    className="max-w-full max-h-[80vh] object-contain rounded-lg"
                  />
                )}

                {/* Next button */}
                {previewImage.allImages.length > 1 && previewImage.currentIndex < previewImage.allImages.length - 1 && (
                  <button
                    onClick={handleNextImage}
                    className="absolute right-0 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-3 rounded-l-lg transition-all duration-200 z-10"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Product info and image indicators */}
              <div className="mt-4">
                <p className="text-white text-center text-lg font-medium mb-2">
                  {previewImage.name}
                </p>
                
                {/* Image indicators */}
                {previewImage.allImages.length > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-3">
                    {previewImage.allImages.map((media, index) => (
                      <button
                        key={index}
                        onClick={() => setPreviewImage({
                          ...previewImage,
                          currentIndex: index,
                          url: previewImage.allImages[index].url
                        })}
                        className={`transition-all duration-200 ${
                          index === previewImage.currentIndex 
                            ? 'text-white scale-125' 
                            : 'text-white text-opacity-50 hover:text-opacity-75'
                        }`}
                      >
                        {media.type === 'video' ? (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        ) : (
                          <div className={`w-2 h-2 rounded-full bg-current ${
                            index === previewImage.currentIndex ? 'w-8' : ''
                          }`} />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    
  );
};

export default QuickBuy;